/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Container,
  Typography,
  Card,
  Grid,
  Button,
  Chip,
  alpha,
  useTheme,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Autorenew as RotateIcon,
  Block as RevokeIcon,
  DeleteOutline as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Api as ApiIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon,
  WarningAmber as WarningIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getApi, postApi, patchApi, deleteApi } from '../services/api';

// Base pública que consumen los hoteles. Se arma desde la misma URL del backend.
const PUBLIC_API_BASE = `${(process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '')}/api/public/v1`;

const ENDPOINTS = [
  { method: 'GET', path: '/me', desc: 'Verifica la llave y muestra el hotel asociado' },
  { method: 'GET', path: '/hotel', desc: 'Perfil del hotel (nombre, moneda, horarios)' },
  { method: 'GET', path: '/rooms', desc: 'Habitaciones con estado y limpieza' },
  { method: 'GET', path: '/reservations?from=&to=&status=', desc: 'Reservas (sin datos de tarjeta)' },
  { method: 'GET', path: '/metrics/occupancy', desc: 'Ocupación actual' },
  { method: 'GET', path: '/metrics/summary?from=&to=', desc: 'Ingresos y conteos por rango' }
];

const fmtDate = (d) => (d ? new Date(d).toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const AdminApiKeys = () => {
  const theme = useTheme();
  const teal = '#00897b';

  const [loading, setLoading] = useState(true);
  const [hotels, setHotels] = useState([]);
  const [search, setSearch] = useState('');

  // Diálogo de creación
  const [createFor, setCreateFor] = useState(null); // hotel row
  const [newName, setNewName] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [creating, setCreating] = useState(false);

  // Diálogo de llave revelada (una sola vez)
  const [revealed, setRevealed] = useState(null); // { apiKey, hotelName, keyName }

  // Diálogo de documentación
  const [docsOpen, setDocsOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getApi('api/hotel-api-keys');
      if (res?.data && res.data.error === false) {
        setHotels(res.data.hotels || []);
      } else {
        toast.error(res?.data?.message || 'No se pudieron cargar las APIs');
      }
    } catch (e) {
      toast.error('Error cargando las APIs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const stats = useMemo(() => {
    let withApi = 0;
    let activeKeys = 0;
    let totalCalls = 0;
    hotels.forEach((h) => {
      if (h.activeKeyCount > 0) withApi += 1;
      (h.apiKeys || []).forEach((k) => {
        if (k.status === 'active') activeKeys += 1;
        totalCalls += k.requestCount || 0;
      });
    });
    return { totalHotels: hotels.length, withApi, activeKeys, totalCalls };
  }, [hotels]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return hotels;
    return hotels.filter((h) => `${h.name} ${h.email}`.toLowerCase().includes(q));
  }, [hotels, search]);

  const copy = async (text, label = 'Copiado') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.info('Copia manual: ' + text);
    }
  };

  const openCreate = (hotel) => {
    setCreateFor(hotel);
    setNewName('');
    setNewExpiry('');
  };

  const submitCreate = async () => {
    if (!newName.trim()) {
      toast.warn('Ponle un nombre a la llave (ej. "IA Recepción")');
      return;
    }
    setCreating(true);
    try {
      const body = { hotelId: createFor.hotelId, name: newName.trim() };
      if (newExpiry) body.expiresAt = newExpiry;
      const res = await postApi('api/hotel-api-keys', body);
      if (res?.data && res.data.error === false) {
        setCreateFor(null);
        setRevealed({ apiKey: res.data.apiKey, hotelName: createFor.name, keyName: newName.trim() });
        loadData();
      } else {
        toast.error(res?.response?.data?.message || res?.data?.message || 'No se pudo crear la llave');
      }
    } catch (e) {
      toast.error('Error creando la llave');
    } finally {
      setCreating(false);
    }
  };

  const rotate = async (hotelName, key) => {
    if (!window.confirm(`¿Rotar la llave "${key.name}"? La llave anterior dejará de funcionar de inmediato.`)) return;
    const res = await postApi(`api/hotel-api-keys/${key._id}/rotate`, {});
    if (res?.data && res.data.error === false) {
      setRevealed({ apiKey: res.data.apiKey, hotelName, keyName: key.name });
      loadData();
    } else {
      toast.error(res?.data?.message || 'No se pudo rotar');
    }
  };

  const revoke = async (key) => {
    if (!window.confirm(`¿Revocar la llave "${key.name}"? Dejará de autenticar pero quedará en el histórico.`)) return;
    const res = await patchApi(`api/hotel-api-keys/${key._id}/revoke`, {});
    if (res?.data && res.data.error === false) {
      toast.success('Llave revocada');
      loadData();
    } else {
      toast.error(res?.data?.message || 'No se pudo revocar');
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`¿Eliminar definitivamente la llave "${key.name}"? Esta acción no se puede deshacer.`)) return;
    const res = await deleteApi('api/hotel-api-keys/', key._id);
    if (res?.data && res.data.error === false) {
      toast.success('Llave eliminada');
      loadData();
    } else {
      toast.error(res?.data?.message || 'No se pudo eliminar');
    }
  };

  const StatCard = ({ label, value, icon }) => (
    <Card sx={{ p: 2.5, borderRadius: 3, height: '100%', border: `1px solid ${alpha(teal, 0.15)}` }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(teal, 0.1),
            color: teal
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Encabezado */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{ width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(teal, 0.12), color: teal }}
            >
              <ApiIcon />
            </Box>
            <Box>
              <Typography variant="h2" sx={{ fontWeight: 700 }}>
                APIs de Hoteles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Genera llaves de solo lectura para que cada hotel consulte sus datos (ideal para IA)
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Button variant="outlined" startIcon={<CodeIcon />} onClick={() => setDocsOpen(true)} sx={{ borderColor: teal, color: teal }}>
          Ver documentación
        </Button>
      </Stack>

      {/* Métricas */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <StatCard label="Hoteles totales" value={stats.totalHotels} icon={<ApiIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Hoteles con API" value={stats.withApi} icon={<CheckIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Llaves activas" value={stats.activeKeys} icon={<KeyIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Llamadas totales" value={stats.totalCalls.toLocaleString('es')} icon={<CodeIcon />} />
        </Grid>
      </Grid>

      {/* Buscador */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar hotel por nombre o email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, maxWidth: 420 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: teal }} />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary">No hay hoteles que coincidan.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((hotel) => {
            const keys = hotel.apiKeys || [];
            return (
              <Accordion key={hotel.hotelId} disableGutters sx={{ borderRadius: '12px !important', '&:before': { display: 'none' }, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', pr: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }} noWrap>
                        {hotel.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {hotel.email || 'sin email'}
                      </Typography>
                    </Box>
                    {hotel.activeKeyCount > 0 ? (
                      <Chip size="small" label={`${hotel.activeKeyCount} activa${hotel.activeKeyCount > 1 ? 's' : ''}`} sx={{ bgcolor: alpha(teal, 0.12), color: teal, fontWeight: 600 }} />
                    ) : (
                      <Chip size="small" variant="outlined" label="Sin API" />
                    )}
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCreate(hotel);
                      }}
                      sx={{ bgcolor: teal, '&:hover': { bgcolor: '#00695c' }, whiteSpace: 'nowrap' }}
                    >
                      Generar API
                    </Button>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: alpha(theme.palette.background.default, 0.4) }}>
                  {keys.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Este hotel aún no tiene ninguna llave de API. Usa &quot;Generar API&quot; para crear la primera.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Nombre</TableCell>
                          <TableCell>Llave</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Último uso</TableCell>
                          <TableCell align="right">Llamadas</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {keys.map((k) => (
                          <TableRow key={k._id}>
                            <TableCell>{k.name}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {k.masked}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {k.status === 'active' ? (
                                <Chip size="small" label="Activa" sx={{ bgcolor: alpha('#2e7d32', 0.12), color: '#2e7d32', fontWeight: 600 }} />
                              ) : (
                                <Chip size="small" label="Revocada" sx={{ bgcolor: alpha('#d32f2f', 0.1), color: '#d32f2f' }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{fmtDate(k.lastUsedAt)}</Typography>
                            </TableCell>
                            <TableCell align="right">{(k.requestCount || 0).toLocaleString('es')}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                {k.status === 'active' && (
                                  <Tooltip title="Rotar (nuevo secreto)">
                                    <IconButton size="small" onClick={() => rotate(hotel.name, k)}>
                                      <RotateIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {k.status === 'active' && (
                                  <Tooltip title="Revocar">
                                    <IconButton size="small" color="warning" onClick={() => revoke(k)}>
                                      <RevokeIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Eliminar">
                                  <IconButton size="small" color="error" onClick={() => remove(k)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      )}

      {/* ---- Diálogo: crear llave ---- */}
      <Dialog open={!!createFor} onClose={() => !creating && setCreateFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Generar API para {createFor?.name}
          <IconButton onClick={() => setCreateFor(null)} sx={{ position: 'absolute', right: 8, top: 8 }} disabled={creating}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Nombre de la llave"
              placeholder="Ej. IA Recepción, Reportes, Panel externo"
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              helperText="Para identificarla después. Puedes crear varias por hotel."
              autoFocus
            />
            <TextField
              label="Expiración (opcional)"
              type="date"
              fullWidth
              value={newExpiry}
              onChange={(e) => setNewExpiry(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Déjalo vacío para que no expire."
            />
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: alpha('#f59e0b', 0.08), display: 'flex', gap: 1 }}>
              <WarningIcon sx={{ color: '#b45309' }} fontSize="small" />
              <Typography variant="caption" sx={{ color: '#92400e' }}>
                La llave es de <strong>solo lectura</strong> y se mostrará <strong>una sola vez</strong>. Guárdala en un lugar seguro.
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateFor(null)} disabled={creating}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={submitCreate} disabled={creating} startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <KeyIcon />} sx={{ bgcolor: teal, '&:hover': { bgcolor: '#00695c' } }}>
            {creating ? 'Generando...' : 'Generar llave'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Diálogo: llave revelada ---- */}
      <Dialog open={!!revealed} onClose={() => setRevealed(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckIcon sx={{ color: teal }} />
            <span>Llave creada</span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {revealed?.hotelName} · {revealed?.keyName}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: alpha(teal, 0.06), wordBreak: 'break-all' }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 14 }}>{revealed?.apiKey}</Typography>
          </Paper>
          <Button fullWidth startIcon={<CopyIcon />} onClick={() => copy(revealed?.apiKey, 'Llave copiada')} sx={{ mt: 1.5, color: teal }}>
            Copiar llave
          </Button>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2, bgcolor: alpha('#d32f2f', 0.06), display: 'flex', gap: 1 }}>
            <WarningIcon sx={{ color: '#d32f2f' }} fontSize="small" />
            <Typography variant="caption" sx={{ color: '#b71c1c' }}>
              Esta es la única vez que verás la llave completa. Si la pierdes, deberás rotarla.
            </Typography>
          </Paper>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">Ejemplo de uso:</Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: '#0f172a', color: '#e2e8f0', overflowX: 'auto' }}>
            <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: 12, m: 0 }}>
{`curl ${PUBLIC_API_BASE}/metrics/occupancy \\
  -H "Authorization: Bearer ${revealed?.apiKey || 'sk_sego_...'}"`}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setRevealed(null)} sx={{ bgcolor: teal, '&:hover': { bgcolor: '#00695c' } }}>
            Ya la guardé
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Diálogo: documentación ---- */}
      <Dialog open={docsOpen} onClose={() => setDocsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Documentación de la API de solo lectura
          <IconButton onClick={() => setDocsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Cada hotel usa su propia llave en el header <code>Authorization: Bearer sk_sego_...</code>. La API solo responde
            datos del hotel dueño de la llave y únicamente acepta <strong>GET</strong> (solo lectura, sin datos de tarjeta).
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, my: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{PUBLIC_API_BASE}</Typography>
            <IconButton size="small" onClick={() => copy(PUBLIC_API_BASE, 'URL base copiada')}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Método</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Descripción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ENDPOINTS.map((e) => (
                <TableRow key={e.path}>
                  <TableCell>
                    <Chip size="small" label={e.method} sx={{ bgcolor: alpha(teal, 0.12), color: teal, fontWeight: 700 }} />
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{e.path}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{e.desc}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDocsOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminApiKeys;
